<script lang="ts" setup>
import { useRefHistory } from "@vueuse/core";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import {
  Entity,
  ForOwnWorkflowType,
  OrgGroupCreateDto,
  OrgGroupUpdateDto,
  SysOrgGroup,
} from "~/api";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";
import EntityInstanceCreator from "./EntityInstanceCreator.vue";

const myInfoStore = useMyInfoStore();
const entityDefinitionStore = useEntityDefinitionStore();

const props = defineProps<{
  orgGroup: SysOrgGroup;
  availableEntities: Entity[];
  drawerOpen: boolean;
  addingNewOrgGroup: boolean;
}>();

type LocalOrgGroup = SysOrgGroup & {
  guid?: string;
  entityInstanceGuid?: string | null;
  entity?: Entity | null;
};

const emit = defineEmits<{
  (e: "drawerClosed"): void;
  (e: "updated", value: SysOrgGroup): void;
  (e: "added", value: SysOrgGroup): void;
  (e: "deleted", value: SysOrgGroup): void;
  (e: "saveNeeded"): void;
}>();

const extendOrgGroup = (og: SysOrgGroup) => {
  const group: LocalOrgGroup = {
    ...og,
    entityInstanceGuid: undefined, // This property doesn't exist in the current SysOrgGroup type
    entity: undefined, // This property doesn't exist in the current SysOrgGroup type
  };

  selectedEntityGuids.value =
    (group.entities
      ?.map((entity) => entity.guid)
      .filter((guid) => guid !== undefined) as string[]) ?? [];
  return group;
};

const newOrgGroupGuid = emptyGuid;

const selectedEntityGuids = ref<string[]>([]);

const localOrgGroup = ref<LocalOrgGroup>(extendOrgGroup(props.orgGroup));

const addingNewOrgGroup = ref(props.addingNewOrgGroup);

const editing = ref(true);

const entitiesInGroup = computed(() => {
  return (localOrgGroup.value.entities ?? []).sort((a: Entity, b: Entity) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  );
});

const sortedAvailableEntities = computed(() => {
  return props.availableEntities
    .slice()
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
});

const editHistory = useRefHistory(localOrgGroup, { deep: true });

type issueType = {
  d: string;
  o?: string | null;
  n?: string | null;
};

const entities = ref<Entity[]>([]);

const entityInstanceCreatorOpen = ref(false);

const selectedEntityDefinitionGuid = ref<string>(
  localOrgGroup.value.entity?.entityDefinitionGuid ?? "",
);

const selectedEntityInstanceGuid = ref<string | undefined>(
  localOrgGroup.value.entityInstanceGuid ?? undefined,
);

const getEntities = async (entityDefinitionGuid: string) => {
  entities.value = await apiClient.entity.getEntities(
    localOrgGroup.value.sysOrgGuid,
    entityDefinitionGuid,
  );
};

const changesToSave = computed(() => {
  const issues: issueType[] = [];
  const originalOrgGroup = editHistory.history.value.at(-1)?.snapshot;

  if (originalOrgGroup) {
    if (originalOrgGroup.name !== localOrgGroup.value.name) {
      issues.push({
        d: "Name changed",
        o: originalOrgGroup.name,
        n: localOrgGroup.value.name,
      });
    }
    if (
      originalOrgGroup.forOwnWorkflow !== localOrgGroup.value.forOwnWorkflow
    ) {
      issues.push({
        d: "For Own Workflow changed",
        o: `${originalOrgGroup.forOwnWorkflow}`,
        n: `${localOrgGroup.value.forOwnWorkflow}`,
      });
    }

    const entitiesChanged =
      selectedEntityGuids.value.length !== originalOrgGroup.entities?.length ||
      originalOrgGroup.entities?.some(
        (entity, index) => entity.guid !== selectedEntityGuids.value[index],
      );
    if (entitiesChanged) {
      issues.push({
        d: "Entities changed",
      });
    }

    if (
      originalOrgGroup.entityInstanceGuid !== selectedEntityInstanceGuid.value
    ) {
      issues.push({
        d: "Entity instance changed",
        o: originalOrgGroup.entityInstanceGuid,
        n: selectedEntityInstanceGuid.value,
      });
    }
  } else {
    issues.push({ d: "New User" });
  }
  return issues;
});

const saveNeeded = computed(() => changesToSave.value.length > 0);

watch(saveNeeded, (newSaveNeeded: boolean) => {
  if (newSaveNeeded) {
    emit("saveNeeded");
  }
});

watch(
  () => entityInstanceCreatorOpen.value,
  async (newVal) => {
    if (!newVal && selectedEntityDefinitionGuid.value) {
      await getEntities(selectedEntityDefinitionGuid.value);
    }
  },
);

const canSaveIssues = computed(() => {
  const issues: issueType[] = [];
  if (!localOrgGroup.value.name) {
    issues.push({
      d: "Name needed",
    });
  }

  return issues;
});
const canSave = computed(() => canSaveIssues.value.length === 0);

const setNewToOriginal = () => {
  editHistory.clear();
};

// const handleEntityDefinitionChange = (newEntityDefinition: string) => {
//   selectedEntityInstanceGuid.value = undefined;
//   getEntities(newEntityDefinition);
// };

const saveChanges = async () => {
  const sysOrgGuid = localOrgGroup.value.sysOrgGuid;

  if (!localOrgGroup.value.name || !sysOrgGuid) {
    return;
  }

  // send to server
  if (addingNewOrgGroup.value) {
    const orgGroupToSave: OrgGroupCreateDto = {
      sysOrgGuid: sysOrgGuid,
      name: localOrgGroup.value.name,
      forOwnWorkflow: localOrgGroup.value.forOwnWorkflow,
      entityGuids: selectedEntityGuids.value,
    };

    await apiClient.orgGroup
      .createOrgGroup(orgGroupToSave)
      .then((res) => {
        ElMessage.success("Saved");
        emit("added", res);
        // editing.value = false;
      })
      .catch(showError);
  } else {
    const groupGuid = localOrgGroup.value.guid;
    if (!groupGuid) {
      return;
    }

    const orgGroupToUpdate: OrgGroupUpdateDto = {
      sysOrgGuid: sysOrgGuid,
      name: localOrgGroup.value.name,
      forOwnWorkflow: localOrgGroup.value.forOwnWorkflow,
      entityGuids: selectedEntityGuids.value,
    };
    await apiClient.orgGroup
      // otherwise, we just update it
      .updateOrgGroup(
        groupGuid,
        localOrgGroup.value.sysOrgGuid,
        orgGroupToUpdate,
      )
      .then((res) => {
        // editing.value = false;
        ElMessage.success("Saved");
        emit("updated", res);
      })
      .catch(showError);
  }
  return;
};

const handleDeleteOrgGroup = async (
  orgGroupGuid: string,
  sysOrgGuid: string,
) => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this group?",
    "Delete Group",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!localOrgGroup.value.guid) {
        return;
      }
      await apiClient.orgGroup
        .deleteOrgGroup(orgGroupGuid, sysOrgGuid)
        .then((res) => {
          ElMessage.success("Group deleted");
          emit("drawerClosed");
          emit("deleted", res);
        })
        .catch(showError);
    })
    .catch(async () => {
      // do nothing
    });
};
const onSave = async () => {
  await saveChanges();
  setNewToOriginal();
  localOrgGroup.value = extendOrgGroup(props.orgGroup);
};

// const getEntityLabel = (entityInstanceGuid: string) => {
//   return (
//     entities.value?.find((entity) => entity.guid === entityInstanceGuid)
//       ?.name ?? ""
//   );
// };

const handleAddEntity = async (entity: Entity) => {
  selectedEntityInstanceGuid.value = entity.guid;
};

onMounted(async () => {
  await Promise.all([myInfoStore.load(), entityDefinitionStore.load()]);
  localOrgGroup.value = extendOrgGroup(props.orgGroup);
  // if (props.orgGroup.guid !== newOrgGroupGuid) {
  //   editing.value = false;
  // } else {
  //   editing.value = true;
  // }

  if (selectedEntityDefinitionGuid.value) {
    getEntities(selectedEntityDefinitionGuid.value);
  }
});
</script>

<template>
  <div v-if="orgGroup" class="org-group-details">
    <div class="edit-buttons">
      <el-button v-if="!editing" type="success" @click="editing = true">
        Edit
      </el-button>

      <el-button
        v-if="editing"
        type="primary"
        :disabled="!saveNeeded || !canSave"
        @click="onSave"
        >Save</el-button
      >
    </div>
    <section class="section core">
      <table class="org-group-details">
        <tbody>
          <tr>
            <td>Group Name</td>
            <td>
              <el-input
                v-if="editing"
                v-model="localOrgGroup.name"
                style="width: 50%"
              />
              <span v-else>{{ orgGroup.name }}</span>
            </td>
          </tr>
          <tr>
            <td>For Own Workflow?</td>
            <td>
              <el-radio-group
                v-model="localOrgGroup.forOwnWorkflow"
                :disabled="!editing"
                size="small"
              >
                <el-radio-button
                  label="Yes - only for their own workflows"
                  :value="ForOwnWorkflowType.OWN"
                />
                <el-radio-button
                  label="No - only for others"
                  :value="ForOwnWorkflowType.OTHERS"
                />
                <el-radio-button label="N/A" :value="ForOwnWorkflowType.NA" />
              </el-radio-group>
              <span class="comment">
                When viewing a workflow, should this Group be applied?
              </span>
            </td>
          </tr>
          <tr>
            <td>Entities</td>
            <td>
              <div v-if="!editing">
                <div v-if="entitiesInGroup.length" class="user-tags">
                  <el-tag v-for="entity in entitiesInGroup" :key="entity.guid">
                    {{ entity.name }}
                  </el-tag>
                </div>

                <span v-else>-</span>
              </div>
              <div v-else>
                <el-transfer
                  v-model="selectedEntityGuids"
                  :data="sortedAvailableEntities"
                  :titles="['Available Entities', 'Entities In Group']"
                  :filterable="true"
                  :props="{
                    key: 'guid',
                    label: 'name',
                  }"
                  :filter-placeholder="'Search'"
                  :disabled="!editing"
                >
                  <template #left-empty>
                    <el-empty
                      :image-size="60"
                      description="No Entities Available"
                    />
                  </template>
                  <template #right-empty>
                    <el-empty
                      :image-size="60"
                      description="No Entities Added"
                    />
                  </template>
                </el-transfer>
              </div>
            </td>
          </tr>
          <!-- <tr>
            <td>Entity Instance</td>
            <td>
              <div v-if="!editing">
                {{
                  selectedEntityInstanceGuid
                    ? `${getEntityLabel(selectedEntityInstanceGuid)} (${entityDefinitionStore.nameOfType(selectedEntityDefinitionGuid)})`
                    : "-"
                }}
              </div>
              <div v-else>
                <div>
                  <el-select
                    v-model="selectedEntityDefinitionGuid"
                    :empty-values="[null, undefined]"
                    style="width: 240px"
                    clearable
                    @change="
                      handleEntityDefinitionChange(selectedEntityDefinitionGuid)
                    "
                  >
                    <el-option
                      v-for="entity in entityDefinitionStore.EntitiesDefinitionList"
                      :key="entity.guid"
                      :label="entity.name ?? undefined"
                      :value="entity.guid"
                    />
                  </el-select>
                </div>
                <div style="margin-top: 5px">
                  <el-select
                    v-if="selectedEntityDefinitionGuid"
                    v-model="selectedEntityInstanceGuid"
                    :empty-values="[null, undefined]"
                    style="width: 240px"
                    clearable
                  >
                    <el-option
                      v-for="entity in entities"
                      :key="entity.guid"
                      :label="entity.name ?? undefined"
                      :value="entity.guid"
                    />
                  </el-select>
                </div>
                <div>
                  <el-button
                    v-if="selectedEntityDefinitionGuid"
                    :icon="CirclePlus"
                    class="addButton"
                    style="margin-top: 5px"
                    @click="entityInstanceCreatorOpen = true"
                  >
                    New
                    {{
                      entityDefinitionStore.nameOfType(
                        selectedEntityDefinitionGuid ?? undefined,
                      )
                    }}</el-button
                  >
                </div>
              </div>
            </td>
          </tr> -->
        </tbody>
      </table>
      <div class="deleteGroup">
        <el-button
          v-if="
            editing &&
            localOrgGroup.guid &&
            localOrgGroup.guid != newOrgGroupGuid
          "
          type="danger"
          plain
          @click="
            handleDeleteOrgGroup(localOrgGroup.guid, localOrgGroup.sysOrgGuid)
          "
          >Delete Group</el-button
        >
      </div>
    </section>
  </div>

  <EntityInstanceCreator
    v-if="selectedEntityDefinitionGuid && entityInstanceCreatorOpen"
    :key="selectedEntityDefinitionGuid"
    v-model="entityInstanceCreatorOpen"
    :entity-definition-guid="selectedEntityDefinitionGuid"
    @add:entity="handleAddEntity"
  />
</template>

<style lang="less">
.el-tabs,
.el-tabs__content,
.el-tab-pane {
  height: 100%;
}
.org-group-details {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, min-content) auto;
  grid-template-areas:
    "edit"
    "core"
    "capture"
    "copy";
  height: 100%;

  .edit-buttons {
    grid-area: edit;
    display: flex;
    .el-switch + .el-button {
      margin-left: 12px;
    }
  }

  .core {
    grid-area: core;

    .user-tags {
      display: flex;
      gap: 0.5em;
      flex-wrap: wrap;
    }
    .el-transfer-panel__footer {
      .el-button {
        margin-left: 0.5em;
      }
    }

    .sorting-users {
      .item {
        display: flex;
        align-items: center;
        margin: 0.5em 0;
      }

      .actions {
        .arrow-button {
          margin: 0 2px;
          padding: 2px 6px;
          font-size: 12px;
        }
      }

      .add {
        margin-top: 1em;
      }
    }
  }

  .copy-btn {
    grid-area: copy;
    justify-self: end;
    align-self: end;
  }

  table {
    td {
      padding: 0.5em;
      border-bottom: 1px solid #ebeef5;
    }
  }
  .comment {
    font-size: 0.9rem;
    margin: 0 0 0 1em;
    color: var(--el-color-primary-light-3);
  }
  .deleteGroup {
    display: flex;
    justify-content: right;
    position: absolute;
    bottom: 1em;
    width: 98%;
  }
  .org-group-details {
    tr {
      td:first-child {
        font-weight: 600;
        vertical-align: baseline;
      }
    }
  }
}
</style>
